import {
  DocumentTemplateRead,
  DocumentTypeRead,
  PlaceholdersResponse,
  FieldPath,
  MappingUpdatePayload,
  GenerateRequest,
  PreviewResponse,
  GeneratedDocumentRead,
  TemplateFileUpdateRead,
  ValidatePathResponse
} from './types';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    ...(token ? { 'Authorization': token } : {}),
    'Accept': 'application/json',
  };

  if (options.body && typeof options.body === 'string') {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  const finalHeaders = { ...headers, ...options.headers };

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: finalHeaders,
  });

  if (res.status === 401) {
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

  if (res.status === 204) return null as T;

  return res.json();
}

export async function fetchDocumentTypes(): Promise<DocumentTypeRead[]> {
  return request('/documents/types/');
}

export async function fetchTemplates(typeCode?: string, isActive: boolean = true): Promise<DocumentTemplateRead[]> {
  const params = new URLSearchParams();
  if (typeCode) params.set('type_code', typeCode);
  params.set('is_active', String(isActive));

  return request(`/documents/templates/?${params.toString()}`);
}

export async function uploadTemplate(
  file: File,
  name: string,
  documentTypeId: number,
  description?: string
): Promise<DocumentTemplateRead> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  formData.append('document_type_id', String(documentTypeId));
  if (description) formData.append('description', description);

  return request('/documents/templates/', {
    method: 'POST',
    body: formData,
  });
}

export async function deleteTemplate(templateId: number): Promise<void> {
  return request(`/documents/templates/${templateId}/`, {
    method: 'DELETE',
  });
}

export async function fetchTemplate(id: number): Promise<DocumentTemplateRead> {
  return request(`/documents/templates/${id}/`);
}

export async function updateTemplateFile(id: number, file: File): Promise<TemplateFileUpdateRead> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/documents/templates/${id}/file/`, {
    method: 'PUT',
    body: formData,
  });
}

export async function fetchPlaceholders(id: number): Promise<PlaceholdersResponse> {
  return request(`/documents/templates/${id}/placeholders/`);
}

export async function fetchSchemaFields(search?: string): Promise<FieldPath[]> {
  const url = search
    ? `/documents/schema/fields/?search=${encodeURIComponent(search)}`
    : '/documents/schema/fields/';
  return request(url);
}

export async function fetchSpecialFields(): Promise<FieldPath[]> {
  return request('/documents/schema/special-fields/');
}

export async function validateFieldPath(path: string): Promise<ValidatePathResponse> {
  return request('/documents/schema/validate-path/', {
    method: 'POST',
    body: JSON.stringify({ path }),
  });
}

export async function updateTemplateMapping(id: number, payload: MappingUpdatePayload): Promise<any> {
  return request(`/documents/templates/${id}/mapping/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function validateTemplateMapping(id: number, applicationId: number): Promise<PreviewResponse> {
  return request(`/documents/templates/${id}/validate/`, {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });
}

export async function previewDocument(req: GenerateRequest): Promise<PreviewResponse> {
  return request('/documents/preview/', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function generateDocumentOnly(req: GenerateRequest, fileName: string): Promise<void> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/documents/generate/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': token } : {})
    },
    body: JSON.stringify(req)
  });

  if (!res.ok) throw new Error('Ошибка при тестовой генерации файла');

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `PREVIEW_${fileName}.docx`);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function generateAndSaveDocument(req: GenerateRequest): Promise<GeneratedDocumentRead> {
  return request('/documents/generate-and-save/', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function fetchGeneratedDocuments(
  page: number = 1,
  size: number = 20,
  applicationId?: number,
  templateId?: number,
  status?: string
): Promise<GeneratedDocumentRead[]> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (applicationId) params.set('application_id', String(applicationId));
  if (templateId) params.set('template_id', String(templateId));
  if (status) params.set('status', status);

  return request(`/documents/generated/?${params.toString()}`);
}

export async function fetchGeneratedDocumentDetails(docId: number): Promise<GeneratedDocumentRead> {
  return request(`/documents/generated/${docId}/`);
}

export async function downloadGeneratedDocument(docId: number, fileName: string): Promise<void> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/documents/generated/${docId}/download/`, {
    headers: {
      ...(token ? { 'Authorization': token } : {})
    }
  });

  if (!res.ok) {
    throw new Error('Ошибка при скачивании файла');
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function createDocumentType(payload: {
  name: string;
  code: string;
  description?: string;
  is_active?: boolean;
}): Promise<DocumentTypeRead> {
  return request('/documents/types/', {
    method: 'POST',
    body: JSON.stringify({ is_active: true, ...payload }),
  });
}

export async function updateDocumentType(
  id: number,
  payload: { name?: string; description?: string; is_active?: boolean }
): Promise<DocumentTypeRead> {
  return request(`/documents/types/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteDocumentType(id: number): Promise<void> {
  return request(`/documents/types/${id}/`, {
    method: 'DELETE',
  });
}